import {useCallback, useMemo} from 'react';

import moment from 'moment-timezone';
import {useConfig} from 'src/modules/config';

import {dateToDateString} from './utils';

import type {Period} from 'src/openapi/admin/models/Period';

export const useDateTime = () => {
    const {timeZone} = useConfig();

    const getLocalDate = useCallback(
        (date?: string) => moment(date).tz(timeZone).toDate(),
        [timeZone],
    );
    const getServerDate = useCallback(
        (date?: string) => {
            const mo1 = moment(date);
            const mo2 = moment(date).tz(timeZone);

            const modifiedDate = moment(date);
            const amount = mo1.utcOffset() - mo2.utcOffset();
            modifiedDate.subtract(amount, 'minutes');
            return modifiedDate.toDate();
        },
        [timeZone],
    );

    const today = useMemo(() => getServerDate(), [getServerDate]);

    const getYesterday = useCallback(() => {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        return yesterday;
    }, [today]);

    const isToday = useCallback((date: string) => {
        if (!date?.length) {
            return false;
        }

        const todayString = dateToDateString(today);
        return todayString === date;
    }, [today]);

    const getTodayString = useCallback(() => dateToDateString(today), [today]);

    const isYesterday = useCallback((date: string) => {
        if (!date?.length) {
            return false;
        }

        const yesterdayString = dateToDateString(getYesterday());
        return yesterdayString === date;
    }, [getYesterday]);

    const getMonthPeriod = useCallback<() => Period>(() => {
        return {
            startDate: dateToDateString(new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())),
            endDate: dateToDateString(today),
        };
    }, [today]);

    const getCalendarMonthPeriod = useCallback<() => Period>(() => {
        return {
            startDate: dateToDateString(new Date(today.getFullYear(), today.getMonth(), 1)),
            endDate: dateToDateString(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
        };
    }, [today]);

    const isFutureDate = useCallback((date?: string) => !!date && getLocalDate(date) > today, [getLocalDate, today]);

    const isAfterDate = useCallback((date1: string, date2: string) => {
        return getLocalDate(date1) >= getLocalDate(date2);
    }, [getLocalDate]);

    const isBeforeToday = useCallback((date: string) => {
        const prevDate = getLocalDate(date);
        const diff = moment(today).diff(prevDate, 'days');
        return prevDate < today && !!diff;
    }, [today, getLocalDate]);

    return ({
        today,
        getYesterday,
        isToday,
        isYesterday,
        isFutureDate,
        isAfterDate,
        isBeforeToday,
        getMonthPeriod,
        getCalendarMonthPeriod,
        getLocalDate,
        getTodayString,
    });
};
